
import React from 'react';
import { Candidate } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useCandidateEmploymentHistory } from "@/hooks/useCandidateEmploymentHistory";
import { 
  Briefcase, 
  Building, 
  Calendar, 
  DollarSign,
  TrendingUp,
  Award,
  ExternalLink,
  AlertCircle
} from 'lucide-react';

interface CandidateEmploymentCardProps {
  candidate: Candidate;
}

export const CandidateEmploymentCard: React.FC<CandidateEmploymentCardProps> = ({ candidate }) => {
  const { data: employmentHistory, isLoading, error } = useCandidateEmploymentHistory(candidate.id);

  const currentEmployment = employmentHistory?.find(emp => emp.is_current_position);
  const pastEmployment = employmentHistory?.filter(emp => !emp.is_current_position) || [];
  const placementHistory = employmentHistory?.filter(emp => emp.placed_by_recruiter && emp.placement_fee) || [];
  
  const totalPlacementRevenue = placementHistory.reduce((sum, placement) => sum + (placement.placement_fee || 0), 0);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short' 
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-primary" />
            Employment & Placement History
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <Skeleton className="h-32 w-full" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-primary" />
            Employment & Placement History
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <AlertCircle className="h-4 w-4 mr-2" />
            Failed to load employment history
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-primary" />
          Employment & Placement History
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Current Position */}
        {currentEmployment ? (
          <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-semibold text-blue-900 dark:text-blue-100">{currentEmployment.job_title}</h3>
                <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                  <Building className="h-4 w-4" />
                  <span>{currentEmployment.company_name}</span>
                </div>
              </div>
              <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                Current Position
              </Badge>
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                <Calendar className="h-3 w-3" />
                <span>Since {currentEmployment.start_date ? formatDate(currentEmployment.start_date) : 'Unknown'}</span>
              </div>
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                <DollarSign className="h-3 w-3" />
                <span>{currentEmployment.salary_range || 'Not specified'}</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-4 bg-muted/20 rounded-lg border border-dashed">
            <div className="text-center text-muted-foreground">
              <Briefcase className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No current employment on record</p>
            </div>
          </div>
        )}

        {/* Placement Summary */}
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
            <div className="text-2xl font-bold text-green-700 dark:text-green-300">{placementHistory.length}</div>
            <div className="text-sm text-green-600 dark:text-green-400">Successful Placements</div>
          </div>
          <div className="text-center p-3 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
            <div className="text-2xl font-bold text-green-700 dark:text-green-300">{formatCurrency(totalPlacementRevenue)}</div>
            <div className="text-sm text-green-600 dark:text-green-400">Total Revenue Generated</div>
          </div>
        </div>

        {/* Placement History */}
        <div>
          <h4 className="text-sm font-medium mb-3 flex items-center gap-1">
            <Award className="h-4 w-4" />
            Placement History
          </h4>
          {placementHistory.length > 0 ? (
            <div className="space-y-3">
              {placementHistory.map((placement) => (
                <div key={placement.id} className="p-3 border rounded-lg hover:bg-muted/20 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-medium text-sm">{placement.job_title}</div>
                      <div className="text-sm text-muted-foreground">{placement.company_name}</div>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {formatCurrency(placement.placement_fee || 0)}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {placement.start_date && placement.end_date && (
                      <>
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span>{formatDate(placement.start_date)} - {formatDate(placement.end_date)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <TrendingUp className="h-3 w-3" />
                          <span>Duration: {Math.round((new Date(placement.end_date).getTime() - new Date(placement.start_date).getTime()) / (1000 * 60 * 60 * 24 * 30))} months</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 bg-muted/20 rounded-lg border border-dashed">
              <div className="text-center text-muted-foreground">
                <Award className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No placement history on record</p>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="flex-1">
            <ExternalLink className="h-3 w-3 mr-1" />
            View Full History
          </Button>
          <Button size="sm" variant="outline" className="flex-1">
            <Calendar className="h-3 w-3 mr-1" />
            Add Employment Record
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
